import React, { useEffect, useContext, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import L from 'leaflet'
import millify from 'millify'
import { debounce } from '@mui/material/utils'
import { useTheme } from '@mui/material/styles'
import CircularProgress from '@mui/material/CircularProgress'
import useMediaQuery from '@mui/material/useMediaQuery'

import UserContext from '../../store/app/Context'
import { store } from '../../store/app/Store'
import MapOverlay from 'components/common/MapOverlay'
import * as mapConsts from './const'
import { loadGoogleMapsLibraries } from 'utils/googleMapsLoader'
import getUserCoordinates from 'utils/getUserCoordinates'

import 'leaflet.gridlayer.googlemutant'
import 'leaflet/dist/leaflet.css'
import '../../styles/leafletMap.scss'

import storageList from 'utils/storageList'
import marker_single from '../../assets/map_markers_new/marker_single.svg'
import marker_single_visited from '../../assets/map_markers_new/marker_single_visited.svg'
import marker_cluster from '../../assets/map_markers_new/marker_cluster.svg'
import marker_lease from '../../assets/map_markers_new/lease_single.svg'
import marker_lease_visited from '../../assets/map_markers_new/lease_single_visited.svg'
import lease_cluster from '../../assets/map_markers_new/lease_cluster.svg'
import sold_single from '../../assets/map_markers_new/sold_single.svg'
import sold_single_visited from '../../assets/map_markers_new/sold_single_visited.svg'
import marker_home from '../../assets/map_markers_new/marker_home.svg'

import { soldStatuses, leasedStatuses, activeStatuses, deListedStatuses } from 'utils/constants'
const expiredListings = [...soldStatuses, ...leasedStatuses, ...deListedStatuses]

let map = null,
  streetAddressPin = null

const LeafletMap = (props) => {
  const theme = useTheme()
  const provider = useContext(UserContext)
  const globalState = useContext(store)
  const { state, dispatch } = globalState

  const { coordinates, mlsId, boardId } = useParams()

  // The z param is used to set the zoom level of the map, if available
  const [params] = useSearchParams(),
    z = params?.get('z')
  const navigate = useNavigate()

  const [listing, setListing] = useState({})
  const [showOverlay, setShowOverlay] = useState(false)

  const baseLayerRef = useRef(null)
  const activeProviderRef = useRef('google')
  const providerRequestIdRef = useRef(0)
  const googleTileErrorCountRef = useRef(0)
  const googleTileErrorResetTimeoutRef = useRef(null)

  const [localSettings, setLocalSettings] = useState(state.settings)
  const [localSearchParams, setLocalSearchParams] = useState(state.searchParams)

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))

  // Ref to track the latest fetch request and cancel stale ones
  const fetchIdRef = React.useRef(0)

  const fetch = useMemo(
    () =>
      debounce(async (request) => {
        await request()
      }, 300),
    []
  )

  // Debounced & memoized function to handle map.setView action
  const setMapView = useMemo(
    () =>
      debounce((location, zoom = map.getZoom()) => {
        map.setView(location, zoom)
      }, 0),
    []
  )

  // Debounced & memoized function to handle map.setZoom action
  const setMapZoom = useMemo(
    () =>
      debounce((zoom) => {
        map.setZoom(zoom)
      }, 0),
    []
  )

  /**
   * A simple function to hide the map popup overlay
   * we need this as a standalone function so that we
   * can reference it in the map.on('move) event &
   * also to cleanup the event listener using the same
   * reference.
   *
   * NOTE: The memory reference is handled by the
   * leaflet library itself, so we don't need to
   * worry about it.
   * @returns void
   */
  const hideOverlay = () => {
    try {
      setShowOverlay(false)

      // if the path has coordinates, then we need to reset the map view
      // if (coordinates && mlsId && boardId) navigate('/map', { replace: true })
    } catch (err) {
      console.error('error hiding the overlay: ', err)
    }
  }

  /**
   * Function to fetch cluster data based on map bounds & render the pins on the map.
   * @returns void
   */
  const fetchClusterData = async () => {
    // Increment fetch ID so we can detect if a newer fetch has started
    const currentFetchId = ++fetchIdRef.current

    dispatch({ type: 'pinsStatus', newVal: 'loading' })

    let combinedClusters = []
    let combinedListings = []
    let totalCount = 0

    try {
      // Fetch active listings if showActiveListings is true
      if (state.showActiveListings) {
        const activeData = await fetchActiveListings()
        if (fetchIdRef.current !== currentFetchId) return // stale, bail out
        if (activeData) {
          combinedClusters = [
            ...combinedClusters,
            ...(activeData.clusters || []),
          ]
          combinedListings = [...combinedListings, ...(activeData.data || [])]
          totalCount += activeData.count || 0
        }
      }

      // Fetch sold listings if showSoldListings is toggled on, or automatically at deep zoom (> 18)
      const isDeepZoom = map.getZoom() > 18
      const shouldFetchSold = isDeepZoom || (
        state.showSoldListings &&
        Object.keys(state.searchParamsSold).length > 0
      )
      if (shouldFetchSold) {
        const soldData = await fetchSoldListings()
        if (fetchIdRef.current !== currentFetchId) return // stale, bail out
        if (soldData) {
          combinedClusters = [...combinedClusters, ...(soldData.clusters || [])]
          combinedListings = [...combinedListings, ...(soldData.data || [])]
          totalCount += soldData.count || 0
        }
      }

      // Prepare data for drawerListings (listings without clusters)
      const listingsOnlyData = await fetchListingsWithoutClusters()
      if (fetchIdRef.current !== currentFetchId) return // stale, bail out

      dispatch({
        type: 'drawerListings',
        newVal: {
          status: 'loaded',
          data: listingsOnlyData,
          count: totalCount,
        },
      })

      // Clear existing markers only right before drawing new ones
      map.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          map.removeLayer(layer)
        }
      })

      // At zoom > 16, always show individual pins (no clusters)
      const showClusters = map.getZoom() <= 16

      if (!showClusters) {
        drawMarkers(combinedListings, false) // Pass false for cluster mode
      } else {
        drawMarkers(combinedClusters, true) // Pass true for cluster mode
      }

      // If streetAddressPin is available, then add it to the map
      if (streetAddressPin) streetAddressPin.addTo(map)
    } catch (error) {
      console.error('Error fetching cluster data:', error)
    } finally {
      if (fetchIdRef.current === currentFetchId) {
        dispatch({ type: 'pinsStatus', newVal: 'loaded' })
      }
    }
  }

  /**
   * Helper function to prepare and fetch active listings data.
   * @returns {Promise<Object|null>} Fetched active data or null on error.
   */
  const fetchActiveListings = async () => {
    let reqData = mapConsts.getMapPinsRequestData(map, state.searchParams)

    if (!reqData) return null

    // this ensures the pins are within the boundary & no confusion with the explicit area/city params
    delete reqData.area
    delete reqData.city

    if (state.polygon.path) {
      if (state.polygon.type === 'city') {
        reqData.city = [state.polygon.name]
      }
      if (state.polygon.type === 'neighborhood') {
        reqData.neighborhood = [state.polygon.name]
      }
    }

    reqData.listings = map.getZoom() >= 16

    try {
      const response = await provider.fetchMapPinsListings(reqData)
      return response
    } catch (error) {
      console.error('Error fetching active listings:', error)
      return null
    }
  }

  /**
   * Helper function to prepare and fetch sold listings data.
   * @returns {Promise<Object|null>} Fetched sold data or null on error.
   */
  const fetchSoldListings = async () => {
    // If user hasn't toggled sold on, build default sold params from active search params
    let soldParams = state.searchParamsSold
    if (!soldParams || Object.keys(soldParams).length === 0) {
      soldParams = {
        ...state.searchParams,
        status: ['U'],
        lastStatus: [...soldStatuses, ...leasedStatuses],
        minSoldDate: 0,
        minListDate: null,
      }
    }

    let reqDataSold = mapConsts.getMapPinsRequestData(map, soldParams)

    if (!reqDataSold) return null

    delete reqDataSold.area
    delete reqDataSold.city

    if (state.polygon.path) {
      if (state.polygon.type === 'city') {
        reqDataSold.city = [state.polygon.name]
      }
      if (state.polygon.type === 'neighborhood') {
        reqDataSold.neighborhood = [state.polygon.name]
      }
    }

    reqDataSold.listings = map.getZoom() >= 16

    try {
      const responseSold = await provider.fetchMapPinsListings(reqDataSold)
      return responseSold
    } catch (error) {
      console.error('Error fetching sold listings:', error)
      return null
    }
  }

  /**
   * Helper function to prepare and fetch listings data for the drawer (without clusters).
   * @returns {Promise<Array>} Combined listings data or empty array on error.
   */
  const fetchListingsWithoutClusters = async () => {
    const cleanRequestData = (data) => {
      if (!data) return null

      const {
        area,
        city,
        listings,
        aggregates,
        clusterPrecision,
        clusterLimit,
        ...cleanedData
      } = data

      if (state.polygon.path) {
        if (state.polygon.type === 'city')
          cleanedData.city = [state.polygon.name]
        if (state.polygon.type === 'neighborhood')
          cleanedData.neighborhood = [state.polygon.name]
      }

      return cleanedData
    }

    let combinedListingsOnly = []

    // Fetch active listings without clusters if showActiveListings is true
    if (state.showActiveListings) {
      const reqDataWithoutClusters = cleanRequestData(
        mapConsts.getMapPinsRequestData(map, state.searchParams)
      )
      try {
        const responseWithoutClusters = await provider.fetchMapPinsListings(
          reqDataWithoutClusters
        )
        combinedListingsOnly = [...(responseWithoutClusters?.data || [])]
      } catch (error) {
        console.error('Error fetching active listings without clusters:', error)
      }
    }

    // Fetch sold listings without clusters if showSoldListings is toggled on, or at deep zoom (> 18)
    const isDeepZoom = map.getZoom() > 18
    const shouldFetchSold = isDeepZoom || (
      state.showSoldListings &&
      Object.keys(state.searchParamsSold).length > 0
    )
    if (shouldFetchSold) {
      let soldParams = state.searchParamsSold
      if (!soldParams || Object.keys(soldParams).length === 0) {
        soldParams = {
          ...state.searchParams,
          status: ['U'],
          lastStatus: [...soldStatuses, ...leasedStatuses],
          minSoldDate: 0,
          minListDate: null,
        }
      }
      const reqDataSoldWithoutClusters = cleanRequestData(
        mapConsts.getMapPinsRequestData(map, soldParams)
      )
      try {
        const responseSoldWithoutClusters = await provider.fetchMapPinsListings(
          reqDataSoldWithoutClusters
        )
        if (responseSoldWithoutClusters?.data) {
          combinedListingsOnly = [
            ...combinedListingsOnly,
            ...responseSoldWithoutClusters.data,
          ]
        }
      } catch (error) {
        console.error('Error fetching sold listings without clusters:', error)
      }
    }

    return combinedListingsOnly
  }

  /**
   * Helper function to draw markers on the map.
   * @param {Array} data - The array of listings or clusters to draw.
   * @param {boolean} isClusterMode - True if drawing clusters, false for individual listings.
   */
  const drawMarkers = (data, isClusterMode) => {
    data.forEach((item) => {
      const listing = isClusterMode ? item.listing : item

      // --- NEW GUARD CLAUSE ---
      // If we're in a cluster and the listing is undefined, we can't get status data.
      // However, clusters themselves have a count, so we can still render them.
      // We only need to check for the listing if we're dealing with a single-item marker.
      if (!listing && (isClusterMode ? item.count === 1 : true)) {
        console.warn('Skipping marker due to missing listing data:', item)
        return // Skip this item if it should be a single marker but has no listing data
      }
      // --- END NEW GUARD CLAUSE ---

      let leafletLocation = null

      if (isClusterMode) {
        if (
          item.location &&
          typeof item.location.latitude === 'string' &&
          typeof item.location.longitude === 'string'
        ) {
          const lat = parseFloat(item.location.latitude)
          const lng = parseFloat(item.location.longitude)
          if (!isNaN(lat) && !isNaN(lng)) {
            leafletLocation = { lat: lat, lng: lng }
          }
        } else if (
          item.location &&
          typeof item.location.latitude === 'number' &&
          typeof item.location.longitude === 'number'
        ) {
          leafletLocation = {
            lat: item.location.latitude,
            lng: item.location.longitude,
          }
        }
      } else {
        if (
          item.map &&
          typeof item.map.latitude === 'string' &&
          typeof item.map.longitude === 'string'
        ) {
          const lat = parseFloat(item.map.latitude)
          const lng = parseFloat(item.map.longitude)
          if (!isNaN(lat) && !isNaN(lng)) {
            leafletLocation = { lat: lat, lng: lng }
          }
        } else if (
          item.map &&
          typeof item.map.latitude === 'number' &&
          typeof item.map.longitude === 'number'
        ) {
          leafletLocation = { lat: item.map.latitude, lng: item.map.longitude }
        }
      }

      if (
        !leafletLocation ||
        typeof leafletLocation.lat !== 'number' ||
        typeof leafletLocation.lng !== 'number'
      ) {
        console.warn(
          'Skipping marker due to invalid or malformed location data after parsing:',
          item
        )
        return
      }

      // Move the declarations inside the conditional blocks to avoid errors
      let imgSrc, labelText, labelClass

      if (isClusterMode && item.count !== 1) {
        // This is a multi-item cluster
        imgSrc = globalState.state.searchParams.type.includes('lease')
          ? lease_cluster
          : marker_cluster
        labelText = item.count.toString()
        labelClass = 'custom-marker-icon__label'
      } else {
        // This is a single pin (either a cluster with count 1 or a standalone marker)
        const isLease = globalState.state.searchParams.type.includes('lease')
        const isSoldOrLeased = listing
          ? [...soldStatuses, ...leasedStatuses].includes(listing.lastStatus)
          : false
        const isVisited = listing
          ? storageList.includes(listing.mlsNumber)
          : false
        const isExpiredOrNotActive = listing
          ? expiredListings.includes(listing.lastStatus) &&
            !activeStatuses.includes(listing.lastStatus)
          : false

        imgSrc = isLease
          ? isVisited
            ? marker_lease_visited
            : isExpiredOrNotActive
            ? sold_single
            : marker_lease
          : isVisited
          ? isExpiredOrNotActive
            ? sold_single_visited
            : marker_single_visited
          : isExpiredOrNotActive
          ? sold_single
          : marker_single
        labelText = `$${
          isSoldOrLeased
            ? millify(listing?.soldPrice || listing?.listPrice, {
                precision: 2,
                decimalSeparator: '.',
                units: ['', 'K', 'M', 'B', 'T'],
                space: false,
              })
            : millify(listing?.listPrice, {
                precision: 2,
                decimalSeparator: '.',
                units: ['', 'K', 'M', 'B', 'T'],
                space: false,
              })
        }`
        labelClass = 'custom-marker-icon__detailed-label'
      }

      let customIcon

      // Sold/leased listings get a faint pill-style rectangle instead of SVG pins (only passive auto-shown at deep zoom)
      if (
        !(isClusterMode && item.count !== 1) &&
        listing &&
        expiredListings.includes(listing.lastStatus) &&
        !activeStatuses.includes(listing.lastStatus) &&
        map.getZoom() > 18 &&
        !state.showSoldListings
      ) {
        customIcon = L.divIcon({
          className: 'sold-marker-icon',
          html: `<div class="sold-marker-pill">${labelText}</div>`,
          iconSize: null,
          iconAnchor: [0, 0],
        })
      } else {
        customIcon = L.divIcon({
          className: 'custom-marker-icon',
          html: `<img ${
            (isClusterMode && item?.count === 1) || !isClusterMode
              ? 'style="width: 59px; margin: -23px 0 0 -23px"'
              : ''
          } src='${imgSrc}' />
        <div class=${labelClass}>${labelText}</div>`,
        })
      }

      const marker = L.marker(leafletLocation, { icon: customIcon }).addTo(map)

      marker.on('click', () => {
        if (isClusterMode && item?.count !== 1) {
          setMapView(marker.getLatLng(), map.getZoom() + 2)
        } else {
          map.off('move click')
          if (listing?.mlsNumber) storageList.add(listing.mlsNumber)
          if (listing?.mlsNumber && listing?.boardId)
            navigate(
              `/map/${leafletLocation?.lat};${leafletLocation?.lng}/${listing?.mlsNumber}/${listing?.boardId}`
            )
          if (mlsId === listing?.mlsNumber) setShowOverlay(true)
          setMapView(marker.getLatLng(), map.getZoom())
        }
      })
    })
  }

  /**
   * Function to handle map load and pan events
   * @returns void
   */
  const handleMapEvents = async () => {
    await fetch(() => fetchClusterData())
  }

  const clearBaseLayer = (targetMap) => {
    if (!targetMap || !baseLayerRef.current) return

    try {
      targetMap.removeLayer(baseLayerRef.current)
    } catch (error) {
      console.warn('Unable to remove existing base layer:', error)
    } finally {
      baseLayerRef.current = null
    }
  }

  const applyOpenStreetMapFallback = (targetMap, reason, error) => {
    if (!targetMap || activeProviderRef.current === 'osm') return

    try {
      clearBaseLayer(targetMap)

      baseLayerRef.current = L.tileLayer(
        mapConsts.openStreetMapTileLayer.url,
        mapConsts.openStreetMapTileLayer.options
      ).addTo(targetMap)

      activeProviderRef.current = 'osm'
      dispatch({ type: 'mapProvider', newVal: 'osm' })

      if (state.satelliteView) {
        dispatch({ type: 'satelliteView', newVal: false })
      }

      console.warn(`Switching to OpenStreetMap fallback: ${reason}`, error)
    } catch (fallbackError) {
      console.error('Failed to switch to OpenStreetMap fallback:', fallbackError)
    }
  }

  const applyGoogleLayer = async (targetMap, satelliteViewEnabled) => {
    const requestId = ++providerRequestIdRef.current
    const hasApiKey = Boolean(mapConsts.loaderOptions?.apiKey)

    if (!hasApiKey) {
      applyOpenStreetMapFallback(targetMap, 'missing_google_maps_api_key')
      return
    }

    try {
      await loadGoogleMapsLibraries(['maps'])

      if (
        requestId !== providerRequestIdRef.current ||
        !targetMap ||
        map !== targetMap
      )
        return

      clearBaseLayer(targetMap)

      const googleLayer = L.gridLayer.googleMutant({
        type: satelliteViewEnabled ? 'satellite' : 'roadmap',
        maxZoom: 21,
        styles: mapConsts.mapStylesArray,
      })

      googleLayer.on('tileerror', (err) => {
        googleTileErrorCountRef.current += 1

        if (googleTileErrorResetTimeoutRef.current) {
          clearTimeout(googleTileErrorResetTimeoutRef.current)
        }

        googleTileErrorResetTimeoutRef.current = setTimeout(() => {
          googleTileErrorCountRef.current = 0
        }, 10000)

        if (googleTileErrorCountRef.current >= 5) {
          applyOpenStreetMapFallback(targetMap, 'google_repeated_tile_errors', err)
        }
      })

      googleLayer.on('error', (err) => {
        applyOpenStreetMapFallback(targetMap, 'google_layer_error', err)
      })

      googleLayer.addTo(targetMap)
      baseLayerRef.current = googleLayer
      activeProviderRef.current = 'google'
      dispatch({ type: 'mapProvider', newVal: 'google' })
    } catch (error) {
      applyOpenStreetMapFallback(targetMap, 'google_maps_loader_failed', error)
    }
  }

  /** Render the map & handle any re-renders based on store changes */
  useEffect(() => {
    let isDisposed = false

    try {
      if (map !== undefined && map !== null) map.remove()

      // Ensure the overlay is hidden on mount & re-renders
      setShowOverlay(false)

      let point = state.settings.center,
        zoom = state.settings.zoom

      if (coordinates) {
        point = {
          lat: Number(coordinates.split(';')[0]),
          lng: Number(coordinates.split(';')[1]),
        }

        if (z) zoom = isNaN(Number(z)) ? 17 : Number(z)
        else zoom = 17
      } else {
        ;(async () => {
          try {
            // if the path has coordinates, then exit
            if (coordinates && mlsId && boardId) return

            const userLocation = await getUserCoordinates()

            point = userLocation
            // console.log('setting zoom: ', state.settings.zoom)
            dispatch({
              type: 'settings',
              newVal: {
                ...state.settings,
                center: userLocation,
                zoom: state.settings.zoom ?? 14,
              },
            })
            // sessionStorage.setItem('userLocation', true)
          } catch (errorTranslationLink) {
            // showToast(errorTranslationLink, 'default')
            console.error('error getting user location: ', errorTranslationLink)
          }
        })()
      }

      // Create a Leaflet map instance
      map = L.map('map1', {
        zoomControl: false, // Remove zoom control buttons from the map
        zoom,
        center: point,
        minZoom: 4,
        maxZoom: 21,
      })

      // hides the default leaflet attribution
      map.attributionControl.setPrefix('')

      // Add a tile layer to the map (using unlicensed google maps data)
      // L.tileLayer('https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
      //   subdomains: ["mt0", "mt1", "mt2", "mt3"],
      //   attribution: '<a href="https://www.google.com/maps"> &copy; Google Maps</a>',
      // }).addTo(map)

      // Add a tile layer to the map (using OpenStreetMap data)
      // L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      //   attribution: 'Map data © OpenStreetMap contributors',
      // }).addTo(map)

      // Add the Google Maps layer using the GoogleMutant plugin
      baseLayerRef.current = L.gridLayer
        .googleMutant({
          type: state.satelliteView ? 'satellite' : 'roadmap',
          maxZoom: 21,
          styles: mapConsts.mapStylesArray,
        })
        .addTo(map)

      // Event listeners for map load and pan events
      // map.on('load', handleMapEvents)
      map.whenReady(handleMapEvents)

      map.on('moveend', () => {
        ;(async () => {
          const center = map.getCenter(),
            zoom = map.getZoom()

          setLocalSearchParams({
            clusterPrecision: zoom + 2,
            clusterLimit: zoom <= 8 ? 200 : 100,
            resultsPerPage: zoom >= 16 ? 200 : 30,
            listings: zoom >= 16 ? true : false,
          })

          setLocalSettings({
            center,
            zoom,
          })
        })()
      })
    } catch (error) {
      console.warn('Something went wrong while rendering the map:', error)
    } finally {
      // Cleanup function to remove event listeners when component unmounts
      return () => {
        isDisposed = true

        if (map !== undefined && map !== null) {
          map.off('load moveend move click')
          map.remove()
        }

        map = null

        baseLayerRef.current = null
        activeProviderRef.current = 'google'
        providerRequestIdRef.current += 1
        googleTileErrorCountRef.current = 0

        if (googleTileErrorResetTimeoutRef.current) {
          clearTimeout(googleTileErrorResetTimeoutRef.current)
          googleTileErrorResetTimeoutRef.current = null
        }

      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Update base layer when satellite mode changes without re-creating the map instance. */
  useEffect(() => {
    if (!map) return
    if (activeProviderRef.current === 'osm') return

    ;(async () => {
      await applyGoogleLayer(map, state.satelliteView)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.satelliteView])

  /** Invalidate map size when the results drawer toggles to avoid layout glitches. */
  useEffect(() => {
    if (!map) return

    setTimeout(() => {
      if (map) map.invalidateSize()
    }, 0)
  }, [state.resultsSection.open])

  /** Listen for changes to localSettings & append updates to the store */
  useEffect(() => {
    ;(async () => {
      dispatch({
        type: 'settings',
        newVal: {
          ...state.settings,
          ...localSettings,
        },
      })

      handleMapEvents()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSettings])

  /** Listen to state.settings.center changes and adjust the map zoom and center */
  useEffect(() => {
    if (map !== undefined && map !== null) {
      // if the center is the same, just exit
      if (map.getCenter().equals(state.settings.center)) return

      // if the center is the same as the localSettings.center, then exit
      // if (map.getCenter().equals(localSettings.center)) return

      // if coordinates are available & the map is already centered on the coordinates, then exit
      if (coordinates) {
        const coords = {
          lat: Number(coordinates.split(';')[0]),
          lng: Number(coordinates.split(';')[1]),
        }

        if (map.getCenter().equals(coords)) return
      }

      setMapView(state.settings.center)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.settings.center])

  /** Listen to state.settings.zoom changes and adjust the map zoom */
  useEffect(() => {
    if (map === undefined || map === null) return

    // if the zoom is the same, just exit
    if (map.getZoom() === state.settings.zoom) return

    setMapZoom(state.settings.zoom)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.settings.zoom])

  /** Listen for changes to localSearchParams & append updates to the store */
  useEffect(() => {
    // console.log('localSearchParams changed:', localSearchParams, state.searchParams)
    const searchParamsWithMapBounds = mapConsts.getMapPinsRequestData(
      map,
      localSearchParams
    )
    dispatch({
      type: 'searchParams',
      newVal: {
        ...state.searchParams,
        ...searchParamsWithMapBounds,
      },
    })

    handleMapEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearchParams])

  /** Listen to state.searchParams changes and re-render the map pins */
  useEffect(() => {
    if (map === undefined || map === null) return

    handleMapEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchParams, state.searchParamsSold])

  /** Listen to changes to coordinates & if not null, then set the map center to the coordinates & zoom to 18 */
  useEffect(() => {
    if (!map) return
    if (!coordinates || !boardId || !mlsId) return

    try {
      const point = {
        lat: Number(coordinates.split(';')[0]),
        lng: Number(coordinates.split(';')[1]),
      }

      setListing({ mlsNumber: mlsId, boardId: boardId })
      setShowOverlay(true)

      // remove the old map.on('move') listener so that the overlay doesn't disappear
      map.off('move click')

      // if the current center is the same as the new center, just exit
      if (map.getCenter().equals(point)) return

      dispatch({
        type: 'settings',
        newVal: { ...state.settings, center: point },
      })
    } catch (err) {
      console.log('error fetching a listing for a point', err)
      navigate('/map')
      setShowOverlay(false)
    }
    // eslint-disable-next-line
  }, [coordinates, mlsId, boardId, window?.Location?.path])

  /** Listen to state.polygon changes & draw a polygon on the map using the bounds & zoom out just a bit to show the entire polygon */
  useEffect(() => {
    // console.log('state.resultsSection.open', state.resultsSection.open)
    if (!map) return

    // Clear all the old polygons
    map.eachLayer((layer) => {
      if (layer instanceof L.Polygon) map.removeLayer(layer)
    })

    if (!state.polygon.name || !state.polygon.type) {
      dispatch({
        type: 'polygon',
        newVal: {
          ...state.polygon,
          path: null,
        },
      })

      handleMapEvents()

      return
    }

    // Fetch the polygon data from provider.fetchPolygonData
    ;(async () => {
      const polygonData = await provider.fetchPolygonData(
        state.polygon.name,
        state.polygon.type
      )
      // const polygonData = require('./polygon.json')

      // if the polygonData is null, then center to the state.polygon.center & exit
      if (!polygonData) {
        dispatch({
          type: 'polygon',
          newVal: {
            ...state.polygon,
            path: null,
          },
        })

        // center to the state.polygon.center
        map.setView(state.polygon.center)

        handleMapEvents()

        return
      }

      const transformedData = polygonData[0].map(([lng, lat]) => [lat, lng])

      // draw the polygon with the polygonData that is [[lng, lat], [lng, lat], ...]
      const polygon = L.polygon(transformedData, {
        color: 'black',
        // transparent fill
        fillColor: '#000',
        fillOpacity: 0,
      }).addTo(map)

      dispatch({
        type: 'polygon',
        newVal: {
          ...state.polygon,
          // set the map param with the polygonData in the format `[[[lng, lat], [lng, lat], ...]]]]`
          path: `[[${polygonData[0].map(([lng, lat]) => `[${lng}, ${lat}]`)}]]`,
        },
      })

      // zoom out just a bit to show the entire polygon
      map.fitBounds(polygon.getBounds().pad(0.01))

      handleMapEvents()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.polygon.name,
    state.polygon.type,
    state.polygon.center,
    state.resultsSection.open,
  ])

  /** If the state.streetAddressMarker.visible is true, then add a marker to the map at the streetAddressMarker.location */
  useEffect(() => {
    if (!map) return

    if (state.streetAddressPin.visible) {
      // A custom pin icon using PushPin material-ui icon
      const customIcon = L.divIcon({
        className: 'custom-marker-icon',
        html: `<img src='${marker_home}'/>`,
        iconSize: [50, 50],
        // offset the pin so that the point is at the bottom middle of the pin
        iconAnchor: [12, 36],
      })

      streetAddressPin = L.marker(state.streetAddressPin.location, {
        icon: customIcon,
        draggable: false,
      }).addTo(map)
    } else {
      if (state.streetAddressPin.marker) {
        map.removeLayer(streetAddressPin)
        streetAddressPin = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.streetAddressPin])

  return (
    <>
      {state.pinsStatus === 'loading' ? (
        <div
          style={{
            maxWidth: theme.spacing(7),
            height: theme.spacing(6),
            minWidth: 0,
            backgroundColor: '#fff',
            '&:hover': {
              backgroundColor: theme.palette.primary.white,
            },
            border: '1px solid ',
            borderColor: theme.palette.primary.main,
            borderRadius: '8px',
            boxShadow: `0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)`,
            position: 'absolute',
            bottom: theme.spacing(10),
            left: theme.spacing(2),
            padding: `1rem 1rem 0.5rem 1rem `,
            zIndex: 999,
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <></>
      )}
      {showOverlay && (
        <MapOverlay
          listing={listing}
          setShowOverlay={setShowOverlay}
          callback={() => {
            map.on('move', hideOverlay)
            map.on('click', hideOverlay)
          }}
          resultsSectionOpen={props.resultsSectionOpen}
        />
      )}
      <div
        id="map1"
        style={
          isMobile
            ? {
                height: `calc(100dvh - 113px)`,
                margin: `0`,
              }
            : { height: `calc(100dvh - 163px)`, margin: `0` }
        }
      ></div>
    </>
  )
}

export default LeafletMap
